<template>
  <span class="tt-wrap">
    <!-- Trigger: a light button with the number of things waiting -->
    <button type="button" class="btn btn-sm tt-trigger" :class="variant === 'outline' ? 'tt-trigger-outline' : 'tt-trigger-light'"
      @click="toggle" title="Work waiting for you today">
      <i class="fe fe-check-square mr-1"></i> Today's tasks
      <span v-if="loaded" class="tt-badge" :class="{ 'tt-badge-zero': !data.total }">{{ formatCount(data.total) }}</span>
      <span v-else-if="loading" class="spinner-border spinner-border-sm ml-1"></span>
    </button>

    <!-- Drawer (moved to <body> in mounted so page headers with transforms don't clip it) -->
    <div ref="drawer" class="tt-root">
      <transition name="tt-fade">
        <div v-if="open" class="tt-backdrop" @click="open = false"></div>
      </transition>
      <transition name="tt-slide">
        <aside v-if="open" class="tt-drawer" role="dialog" aria-label="Today's tasks">
          <header class="tt-head">
            <div>
              <h5 class="mb-0">Today's tasks</h5>
              <small class="text-muted">{{ todayLabel }}<span v-if="data.generated_at"> · updated {{ updatedLabel }}</span></small>
            </div>
            <div class="d-flex align-items-center" style="gap: 4px;">
              <button class="btn btn-sm btn-link text-muted" :disabled="loading" @click="load" title="Refresh">
                <i class="fe fe-refresh-cw" :class="{ 'tt-spin': loading }"></i>
              </button>
              <button class="btn btn-sm btn-link text-muted" @click="open = false" title="Close"><i class="fe fe-x"></i></button>
            </div>
          </header>

          <div class="tt-body">
            <div v-if="error" class="alert alert-danger py-2 small">{{ error }}</div>

            <div v-if="loaded && !pending.length" class="tt-empty">
              <i class="fe fe-smile"></i>
              <p class="mb-0 font-weight-bold">You're all caught up</p>
              <small class="text-muted">Nothing is waiting for you right now.</small>
            </div>

            <div v-if="pending.length" class="tt-summary">
              <b>{{ formatCount(data.total) }}</b> {{ data.total === 1 ? "item needs" : "items need" }} your attention
            </div>

            <div v-for="group in groups" :key="group.name" class="tt-group">
              <div class="tt-group-name">{{ group.name }}</div>
              <div v-for="task in group.tasks" :key="task.key" class="tt-task" :class="'tt-' + task.severity">
                <div class="tt-task-main" @click="toggleTask(task)">
                  <div class="tt-task-text">
                    <div class="tt-task-label">{{ task.label }}</div>
                    <small class="text-muted">{{ task.hint }}</small>
                  </div>
                  <span class="tt-count">{{ formatCount(task.count) }}</span>
                </div>
                <div class="tt-task-actions">
                  <button v-if="task.items.length" class="btn btn-link btn-sm p-0" @click="toggleTask(task)">
                    <i class="fe" :class="expanded[task.key] ? 'fe-chevron-up' : 'fe-chevron-down'"></i>
                    {{ expanded[task.key] ? "Hide" : "Show" }} {{ task.count > task.items.length ? "first " + task.items.length : task.items.length === 1 ? "it" : "them" }}
                  </button>
                  <a v-if="task.link" href="#" class="btn btn-sm tt-open ml-auto" @click.prevent="go(task.link)">
                    Open <i class="fe fe-arrow-right ml-1"></i>
                  </a>
                </div>
                <ul v-if="expanded[task.key] && task.items.length" class="tt-items">
                  <li v-for="(item, i) in task.items" :key="i">
                    <a v-if="item.link" href="#" @click.prevent="go(item.link)">{{ item.title }}</a>
                    <span v-else>{{ item.title }}</span>
                    <small v-if="item.subtitle" class="text-muted d-block">{{ item.subtitle }}</small>
                  </li>
                </ul>
              </div>
            </div>

            <div v-if="done.length" class="tt-done">
              <button class="btn btn-link btn-sm p-0 text-muted" @click="showDone = !showDone">
                <i class="fe fe-check-circle mr-1 text-success"></i> {{ done.length }} all clear
                <i class="fe ml-1" :class="showDone ? 'fe-chevron-up' : 'fe-chevron-down'"></i>
              </button>
              <ul v-if="showDone" class="tt-done-list">
                <li v-for="task in done" :key="task.key">{{ task.label }}</li>
              </ul>
            </div>
          </div>
        </aside>
      </transition>
    </div>
  </span>
</template>

<script>
// "Today's tasks" (GET my-tasks, backend App\Tasks\DailyTasks): the work waiting for the signed-in user, chosen by the
// API from their type, admin flag and job title. Opens by itself once a day on the home page when something is waiting.
const SEEN_KEY = "todayTasksSeen";

export default {
  name: "TodayTasks",
  props: {
    // "light" for dark page headers, "outline" for white cards.
    variant: { type: String, default: "light" },
    autoOpen: { type: Boolean, default: true },
  },
  data() {
    return { open: false, loading: false, loaded: false, error: "", data: { total: 0, tasks: [] }, expanded: {}, showDone: false };
  },
  computed: {
    pending() {
      return (this.data.tasks || []).filter((t) => t.count > 0);
    },
    done() {
      return (this.data.tasks || []).filter((t) => !t.count);
    },
    groups() {
      const order = { danger: 0, warning: 1, info: 2 };
      const map = {};
      this.pending.forEach((t) => {
        (map[t.group] = map[t.group] || []).push(t);
      });
      return Object.keys(map)
        .map((name) => ({ name, tasks: map[name].sort((a, b) => order[a.severity] - order[b.severity]) }))
        .sort((a, b) => Math.min(...a.tasks.map((t) => order[t.severity])) - Math.min(...b.tasks.map((t) => order[t.severity])));
    },
    todayLabel() {
      return new Date().toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" });
    },
    updatedLabel() {
      const d = new Date(String(this.data.generated_at).replace(" ", "T"));
      return isNaN(d) ? "" : d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
    },
  },
  mounted() {
    document.body.appendChild(this.$refs.drawer);
    document.addEventListener("keydown", this.onKey);
    this.load().then(() => this.maybeAutoOpen());
  },
  beforeDestroy() {
    document.removeEventListener("keydown", this.onKey);
    if (this.$refs.drawer && this.$refs.drawer.parentNode) this.$refs.drawer.parentNode.removeChild(this.$refs.drawer);
  },
  methods: {
    load() {
      this.loading = true;
      this.error = "";
      return this.axios
        .get("/api/v1/auth/my-tasks")
        .then((r) => {
          this.data = r.data || { total: 0, tasks: [] };
          this.loaded = true;
          // Open the most urgent task's examples by default.
          const first = this.groups.length ? this.groups[0].tasks[0] : null;
          if (first && first.items.length && !Object.keys(this.expanded).length) this.$set(this.expanded, first.key, true);
        })
        .catch(() => {
          this.error = "Could not load your tasks. Try again in a moment.";
        })
        .finally(() => {
          this.loading = false;
        });
    },
    maybeAutoOpen() {
      if (!this.autoOpen || !this.data.total) return;
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const today = new Date().toISOString().slice(0, 10);
      const key = `${SEEN_KEY}:${user.id || ""}`;
      if (localStorage.getItem(key) === today) return;
      localStorage.setItem(key, today);
      this.open = true;
    },
    toggle() {
      this.open = !this.open;
      if (this.open && !this.loading) this.load();
    },
    toggleTask(task) {
      if (task.items.length) this.$set(this.expanded, task.key, !this.expanded[task.key]);
    },
    go(link) {
      this.open = false;
      if (this.$route.fullPath !== link) this.$router.push(link).catch(() => {});
    },
    onKey(e) {
      if (e.key === "Escape") this.open = false;
    },
    formatCount(n) {
      return Number(n || 0).toLocaleString("en-GB");
    },
  },
};
</script>

<style scoped>
.tt-wrap { display: inline-flex; }
.tt-trigger { display: inline-flex; align-items: center; font-weight: 600; border-radius: 8px; white-space: nowrap; }
.tt-trigger-light { background: rgba(255, 255, 255, 0.95); color: #1e3a5f; border: 1px solid rgba(255, 255, 255, 0.6); }
.tt-trigger-light:hover { background: #fff; color: #2563eb; }
.tt-trigger-outline { background: #fff; color: #1e3a5f; border: 1px solid #cbd5e1; }
.tt-trigger-outline:hover { border-color: #2563eb; color: #2563eb; }
.tt-badge { margin-left: 8px; min-width: 22px; padding: 1px 7px; border-radius: 999px; background: #dc2626; color: #fff; font-size: 0.72rem; line-height: 18px; text-align: center; }
.tt-badge-zero { background: #16a34a; }

.tt-backdrop { position: fixed; inset: 0; background: rgba(15, 23, 42, 0.35); z-index: 1060; }
.tt-drawer { position: fixed; top: 0; right: 0; bottom: 0; width: 420px; max-width: 100vw; background: #f8fafc; z-index: 1061;
  box-shadow: -8px 0 30px rgba(15, 23, 42, 0.18); display: flex; flex-direction: column; font-family: inherit; }
.tt-head { display: flex; justify-content: space-between; align-items: center; padding: 16px 18px; background: #fff; border-bottom: 1px solid #e2e8f0; }
.tt-head h5 { color: #1e3a5f; font-weight: 700; }
.tt-body { flex: 1; overflow-y: auto; padding: 14px 16px 24px; }
.tt-summary { font-size: 0.9rem; color: #334155; margin-bottom: 12px; }
.tt-group { margin-bottom: 14px; }
.tt-group-name { font-size: 0.7rem; text-transform: uppercase; letter-spacing: 0.06em; color: #64748b; font-weight: 700; margin: 0 2px 6px; }
.tt-task { background: #fff; border: 1px solid #e2e8f0; border-left: 4px solid #94a3b8; border-radius: 10px; padding: 10px 12px; margin-bottom: 8px; }
.tt-task.tt-danger { border-left-color: #dc2626; }
.tt-task.tt-warning { border-left-color: #f59e0b; }
.tt-task.tt-info { border-left-color: #2563eb; }
.tt-task-main { display: flex; align-items: flex-start; gap: 10px; cursor: pointer; }
.tt-task-text { flex: 1; min-width: 0; }
.tt-task-label { font-weight: 600; color: #0f172a; font-size: 0.9rem; }
.tt-count { font-weight: 700; font-size: 1.05rem; color: #0f172a; min-width: 32px; text-align: right; }
.tt-danger .tt-count { color: #dc2626; }
.tt-warning .tt-count { color: #b45309; }
.tt-info .tt-count { color: #2563eb; }
.tt-task-actions { display: flex; align-items: center; margin-top: 6px; font-size: 0.8rem; }
.tt-open { background: #eff6ff; color: #2563eb; border-radius: 6px; padding: 2px 10px; font-size: 0.78rem; font-weight: 600; }
.tt-open:hover { background: #2563eb; color: #fff; }
.tt-items { list-style: none; padding: 8px 0 0; margin: 8px 0 0; border-top: 1px dashed #e2e8f0; }
.tt-items li { padding: 4px 0; font-size: 0.84rem; }
.tt-items a { color: #1e3a5f; font-weight: 500; }
.tt-items a:hover { color: #2563eb; }
.tt-empty { text-align: center; padding: 40px 10px; color: #334155; }
.tt-empty .fe { font-size: 36px; color: #16a34a; display: block; margin-bottom: 8px; }
.tt-done { margin-top: 8px; font-size: 0.82rem; }
.tt-done-list { list-style: none; padding: 6px 0 0 20px; margin: 0; color: #64748b; font-size: 0.8rem; }
.tt-spin { display: inline-block; animation: tt-rot 0.9s linear infinite; }
@keyframes tt-rot { to { transform: rotate(360deg); } }
.tt-fade-enter-active, .tt-fade-leave-active { transition: opacity 0.2s; }
.tt-fade-enter, .tt-fade-leave-to { opacity: 0; }
.tt-slide-enter-active, .tt-slide-leave-active { transition: transform 0.25s ease; }
.tt-slide-enter, .tt-slide-leave-to { transform: translateX(100%); }
@media (prefers-reduced-motion: reduce) {
  .tt-slide-enter-active, .tt-slide-leave-active, .tt-fade-enter-active, .tt-fade-leave-active { transition: none; }
}
</style>
